import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import Big from 'big.js';
import LazyTooltip from 'components/LazyTooltip';
import { formatNumber } from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import LabelledData from 'pages/timecards/LabelledData';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { InvoiceTimecard } from 'types/invoice-details';
import { cn } from 'utils/cn';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { InvoiceDetailsParameterizedTracker } from '../../InvoiceDetailsChangeTrackers';
import { CALCULATION, CALCULATION_RESULT, CALCULATION_STEP, CALCULATION_STEP_BOLD } from '../../InvoiceItemRow';
import styles from './TotalAmountDisplay.module.css';

type Props = {
    currency: string;
    invoiceTimecard: InvoiceTimecard;
    decimalScale: 1 | 2;
    isPaymentCurrencyMode?: boolean;
};

const TimecardDetailedCalculations = ({
    invoiceTimecard,
    currency,
    decimalScale,
    isPaymentCurrencyMode,
}: {
    invoiceTimecard: InvoiceTimecard;
    currency: string;
    decimalScale: 1 | 2;
    isPaymentCurrencyMode?: boolean;
}) => {
    const { calculationLog } = invoiceTimecard;
    const log = isPaymentCurrencyMode ? calculationLog?.paymentCurrency : calculationLog;
    const initialAmount = Math.round((log?.initialAmount ?? 0) * 100) / 100;
    // Rates/percentages don't change with currency — always read from invoice currency log
    const timecardDiscountRate = Math.round((calculationLog?.timecardDiscountRate ?? 0) * 100) / 100;
    const timecardDiscountAdjustmentAmount = Math.round((log?.timecardDiscountAdjustmentAmount ?? 0) * 100) / 100;
    const timecardCashAdjustmentAmount = Math.round((log?.timecardCashAdjustmentAmount ?? 0) * 100) / 100;
    const timecardAdjustments = new Big(timecardDiscountAdjustmentAmount + timecardCashAdjustmentAmount).toFixed(2);
    const amountAfterTimecardAdjustments = Math.round((log?.amountAfterTimecardAdjustments ?? 0) * 100) / 100;
    const timecardMatterCashAdjustmentAmount = Math.round((log?.timecardMatterCashAdjustmentAmount ?? 0) * 100) / 100;
    const amountAfterMatterCashAdjustments = Math.round((log?.timecardAmountBeforeMatterDiscount ?? 0) * 100) / 100;
    const matterDiscountRate = calculationLog?.matterDiscountAdjustmentRate ?? 0;
    const matterDiscountAdjustmentAmount = Math.round((log?.matterDiscountAdjustmentAmount ?? 0) * 100) / 100;
    const multiplierRate = calculationLog?.multiplierRate ? Number(calculationLog.multiplierRate) : 1;
    const finalAmount = Math.round((log?.finalAmount ?? 0) * 100) / 100;

    return (
        <div className={CALCULATION}>
            <p className="text-body1">
                <FormattedMessage id="invoices.details.table.initial-amount" />
            </p>
            <div className={CALCULATION_STEP}>
                <LabelledData label="invoices.details.table.hourly-rate">
                    {calculationLog?.hourlyRate ?? '-'}
                </LabelledData>
                <div>*</div>
                <LabelledData label="invoices.details.table.billed">
                    {formatNumber(calculationLog?.billedTimeInHours ?? 0, decimalScale)}
                </LabelledData>
                <div>*</div>
                <LabelledData label="invoices.details.table.multiplier-rate">
                    {multiplierRate === 1 ? multiplierRate.toFixed(1) : multiplierRate.toFixed(2)}
                </LabelledData>
                <div>=</div>
                <div>{initialAmount.toFixed(2)}</div>
            </div>
            <p className="text-body1">
                <FormattedMessage id="invoices.details.table.timecard-adjustments" />
            </p>
            <div className={CALCULATION_STEP}>
                <LabelledData label="invoices.details.table.initial-amount">{initialAmount.toFixed(2)}</LabelledData>
                <div>*</div>
                <LabelledData label="invoices.details.table.timecard-discount-rate">
                    {`-${timecardDiscountRate}`}
                </LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.timecard-cash-adjustments">
                    {timecardCashAdjustmentAmount.toFixed(2)}
                </LabelledData>
                <div>=</div>
                <div>{timecardAdjustments}</div>
            </div>
            <p className="text-body1 font-semibold">
                <FormattedMessage id="invoices.details.table.amount-after-timecard-adjustments" />
            </p>
            <div className={CALCULATION_STEP_BOLD}>
                <LabelledData label="invoices.details.table.initial-amount">{initialAmount.toFixed(2)}</LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.timecard-adjustments">{timecardAdjustments}</LabelledData>
                <div>=</div>
                <div>{amountAfterTimecardAdjustments.toFixed(2)}</div>
            </div>
            <p className="text-body1 font-semibold">
                <FormattedMessage id="invoices.details.table.amount-after-matter-cash-adjustments" />
            </p>
            <div className={CALCULATION_STEP_BOLD}>
                <LabelledData label="invoices.details.table.amount-after-timecard-adjustments">
                    {amountAfterTimecardAdjustments.toFixed(2)}
                </LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.matter-cash-adjustments">
                    {timecardMatterCashAdjustmentAmount.toFixed(2)}
                </LabelledData>
                <div>=</div>
                <div>{amountAfterMatterCashAdjustments.toFixed(2)}</div>
            </div>
            <p className="text-body1">
                <FormattedMessage id="invoices.details.table.matter-discount" />
            </p>
            <div className={CALCULATION_STEP}>
                <LabelledData label="invoices.details.table.amount-after-matter-cash-adjustments">
                    {amountAfterMatterCashAdjustments.toFixed(2)}
                </LabelledData>
                <div>*</div>
                <LabelledData label="invoices.details.table.matter-discount-rate">
                    {`-${matterDiscountRate}`}
                </LabelledData>
                <div>=</div>
                <div>{matterDiscountAdjustmentAmount.toFixed(2)}</div>
            </div>
            <p className="text-h5">
                <FormattedMessage id="invoices.details.table.final-amount" />
            </p>
            <div className={CALCULATION_RESULT}>
                <LabelledData label="invoices.details.table.initial-amount">{initialAmount.toFixed(2)}</LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.timecard-adjustments">{timecardAdjustments}</LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.matter-cash-adjustments">
                    {timecardMatterCashAdjustmentAmount.toFixed(2)}
                </LabelledData>
                <div>+</div>
                <LabelledData label="invoices.details.table.matter-discount">
                    {matterDiscountAdjustmentAmount.toFixed(2)}
                </LabelledData>
                <div>=</div>
                <div>
                    <MoneyDisplay amount={finalAmount} currency={currency} />
                </div>
            </div>
        </div>
    );
};

export const TotalAmountDisplay = React.memo(
    ({ currency, invoiceTimecard, decimalScale, isPaymentCurrencyMode }: Props) => {
        const { calculationLog } = invoiceTimecard;
        const log = isPaymentCurrencyMode ? calculationLog?.paymentCurrency : calculationLog;
        const isFinalAmountFieldEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_DETAILS_SHOW_TIMECARD_FINAL_AMOUNT);

        const currentAmount =
            Math.round(
                (isFinalAmountFieldEnabled ? (log?.finalAmount ?? 0) : (log?.amountAfterTimecardAdjustments ?? 0)) * 100
            ) / 100;
        const amountBeforeAdjustment = Math.round((log?.initialAmount ?? 0) * 100) / 100;
        const isTooltipEnabled = currentAmount !== amountBeforeAdjustment;
        const displayedBeforeAdjustment = useMemo(
            () => new Big(amountBeforeAdjustment).toFixed(2),
            [amountBeforeAdjustment]
        );

        return (
            <div className={`${styles.container} opacity-decreasing-when-disabled`}>
                <div className={styles.headerRow}>
                    <InvoiceDetailsParameterizedTracker
                        currentParam={currentAmount}
                        storedParam={amountBeforeAdjustment}
                        displayValue={`${displayedBeforeAdjustment} ${currency}`}
                    />
                    {isTooltipEnabled ? (
                        <LazyTooltip
                            contentClassName="w-[700px] max-w-[700px] rounded-[7px] text-[11px] new:text-caption"
                            title={
                                <TimecardDetailedCalculations
                                    invoiceTimecard={invoiceTimecard}
                                    currency={currency}
                                    decimalScale={decimalScale}
                                    isPaymentCurrencyMode={isPaymentCurrencyMode}
                                />
                            }
                        >
                            <InfoOutlinedIcon className={cn(styles.infoIcon, 'text-grey-400')} />
                        </LazyTooltip>
                    ) : null}
                </div>
                <MoneyDisplay amount={currentAmount} currency={currency} />
            </div>
        );
    }
);
